/* Source of truth for every translatable UI string: ONE flat object,
   key -> English text. This is the only place new user-facing strings are
   added; components look them up with `t("namespace.key")` (see
   LanguageContext.jsx). The admin "Languages" tab sends this object to the
   backend (POST /api/i18n/languages/:code/generate) to machine-translate
   whatever has no translation yet, so adding a key here is all it takes for
   a new string to become translatable -- no code change elsewhere.

   WHAT BELONGS HERE (static UI chrome only): navigation, buttons, labels,
   headings, system/toast/error messages, static marketing copy.

   WHAT DOES NOT (call it out separately if it ever needs its own flow):
   - Proper nouns and brand names (Hifz with Hikmah, HikmahVerses, surah names)
   - User-generated content: names, halaqa titles, announcements, notes,
     teacher bios, reviews, feedback text
   - Dynamic values: numbers, dates, durations -> format with fmtNumber/fmtDate
     from useLanguage() (Intl, locale-aware), never bake them into a string.
     Splice them in with {placeholders}: t("dashboard.welcome", { name }).
   - Quranic text, tafsir, Ibn Kathir excerpts, AI-generated tadabbur and
     other long-form content bodies -- NOT keys. Tafsir / tadabbur / the
     Memorize excerpt have their own translation flow (backend/content_i18n.py,
     run by the same Generate button); Quranic text is never translated.

   Key rules: dot-notation grouped by feature (nav.*, tabbar.*, errors.*);
   keys match /^[A-Za-z0-9_.-]{1,120}$/; English text <= 2000 chars; use
   {name}-style placeholders (they are protected from machine translation).
   Renaming a key orphans its translations. Changing the English text of a
   key makes its un-reviewed machine translation regenerate on the next
   Generate, but leaves a human-reviewed one alone -- re-check those by hand. */
export const TRANSLATION_KEYS = {
  // ---- navigation (drawer) ----
  "nav.home": "Home",
  "nav.myQuran": "My Quran",
  "nav.tadabbur": "Tadabbur",
  "nav.myHifz": "My Hifz",
  "nav.basicHifz": "Basic Hifz",
  "nav.community": "Community",
  "nav.teachers": "Teachers",
  "nav.myHalaqa": "My Halaqa",
  "nav.profile": "Profile",
  "nav.gradingInbox": "Grading Inbox",
  "nav.halaqaPulse": "Halaqa Pulse",
  "nav.teacherHub": "Mentor Hub",
  "nav.bookings": "Bookings",
  "nav.getVerified": "Get Verified",
  "nav.adminPanel": "Admin Panel",
  "nav.translations": "Translations",
  "nav.school": "School",
  "nav.school_principal": "My School",
  "nav.school_teacher": "My Students",
  "nav.teacherApplications": "Mentor Applications",
  "nav.orgAdmin": "Organization Admin",
  "nav.section.teachers": "For Mentors",
  "nav.section.administration": "Administration",
  "nav.section.orgAdministration": "Organization Administration",
  "nav.openMenu": "Open menu",
  "nav.closeMenu": "Close menu",

  // ---- bottom tab bar ----
  "tabbar.home": "Home",
  "tabbar.quran": "Quran",
  "tabbar.tadabbur": "Tadabbur",
  "tabbar.hifz": "Hifz",
  "tabbar.profile": "Profile",
  "tabbar.primary": "Primary",

  // ---- user card ----
  "user.defaultName": "User",
  "user.role.student": "student",
  "user.role.mentor": "mentor",
  "user.role.teacher": "teacher",
  "user.role.admin": "admin",
  "user.role.principal": "principal",
  "user.role.translator": "translator",
  "user.role.orgAdmin": "Org admin",
  "user.pending": "Pending",
  "user.signOut": "Sign out",
  "user.streak": "Streak",
  "user.ayahs": "Ayahs",
  "user.xp": "XP",

  // ---- home (Dashboard) ----
  "dashboard.greeting": "Assalamu Alaikum",
  "dashboard.friend": "Friend",
  "dashboard.tagline": "Understand it. Reflect on it. Live it.",
  "dashboard.sentYou": "sent you:",
  "dashboard.quranTree": "Your Quran Tree",
  "dashboard.fullLegacy": "Full legacy →",

  // ---- Today's Plan card ----
  "plan.title": "Today's Plan",
  "plan.minutesTotal": "~{minutes} min total",
  "plan.minutes": "~{minutes} min",
  "plan.adjustGoal": "Adjust goal →",
  "plan.newMemorization": "New Memorization",
  "plan.newDetail": "{surah} · Ayahs {start}–{end} ({count})",
  "plan.skippedToday": "Skipped for today",
  "plan.allMemorized": "All available ayahs memorized!",
  "plan.recentMemorization": "Recent Memorization",
  "plan.toReviewOne": "{count} ayah to review",
  "plan.toReviewOther": "{count} ayahs to review",
  "plan.longTermRevision": "Long-Term Revision",
  "plan.dueOne": "{count} ayah due",
  "plan.dueOther": "{count} ayahs due",
  "plan.memorize": "Memorize",
  "plan.review": "Review",
  "plan.revise": "Revise",

  // ---- language picker ----
  "language.label": "Language",
  "language.english": "English",

  // ---- plurals & computed text (hand-converted; t("key", { count }) picks the One/Other form) ----
  "common.ayahCountOne": "{count} ayah",
  "common.ayahCountOther": "{count} ayahs",
  "common.mistakeCountOne": "{count} mistake",
  "common.mistakeCountOther": "{count} mistakes",
  "common.pageN": "Page {n}",
  "common.juzN": "Juz {n}",
  "common.hizbN": "Hizb {n}",
  "tadabur.partsHintOne": "Introduction + {count} thematic section",
  "tadabur.partsHintOther": "Introduction + {count} thematic sections",
  "myQuran.loopProgress": "Loop {loop}/{loops} · Ayah {ayah}/{rangeSize}",
  "myQuran.nowPlayingAyah": "Now playing — Ayah {n}",
  "myQuran.matchesForOne": "match for",
  "myQuran.matchesForOther": "matches for",
  "myQuran.noMatchesInSurah": "No matches for “{query}” in Surah {surah}.",
  "hifzTest.toUncoverNextAyah": "to uncover the next ayah, hover an ayah to peek at it",
  "hifzTest.revealedOfOne": "Revealed {revealed} of {count} ayah.",
  "hifzTest.revealedOfOther": "Revealed {revealed} of {count} ayahs.",
  "hifzTest.extraWordsHeardShortOne": "{count} extra word heard",
  "hifzTest.extraWordsHeardShortOther": "{count} extra words heard",
  "hifzTest.wordsToReviewOne": "{count} word to review",
  "hifzTest.wordsToReviewOther": "{count} words to review",
  "hifzTest.timeLeftToday": "{time} left today",
  "hifzTest.testsLeft": "{left} of {total} AI tests left this month",
  "hifzTest.testsRenew": "Renews on the 1st · up to {minutes} min each",
  "hifzTest.usedAllTests": "You've used all {total} AI tests for this month. They renew on the 1st. AI feedback in the memorise flow is still free.",
  "hifzTest.outsidePlan": "This part of Hifz isn't open on your plan yet. See your Hifz steps.",
  "hifzTest.noAiOnPlan": "AI tests come with a package",
  "hifzTest.aiNeedsPackage": "AI tests come with a package (10 a month). The self-test below is free for everyone.",
  "hifzTest.accuracyMistakesOne": "{accuracy}% accuracy · {count} mistake",
  "hifzTest.accuracyMistakesOther": "{accuracy}% accuracy · {count} mistakes",
  "hifzTest.rangeAyah": "ayah {n}",
  "hifzTest.rangeAyahs": "ayahs {start}–{end}",
  "hifzTest.yourRecitationRange": "Your recitation · {range} — replay before submitting",
  "hifzTest.reciteRangeInOneGo": "Recite {range} in one go — every word reveals green / red on the Mushaf above once you stop. Tap any red word for a tajweed tip.",
  "hifzTest.wordSkipped": "Word skipped",
  "hifzTest.mistake": "Mistake",
  "hifzTest.wordTipHeader": "{kind} · {verseKey} · word {position}",
  "hifzTest.reciteFromMemoryOne": "Recite the {count} ayah from memory. The mushaf reveals word-by-word in green/red the moment you stop.",
  "hifzTest.reciteFromMemoryOther": "Recite the {count} ayahs from memory. The mushaf reveals word-by-word in green/red the moment you stop.",
  "hifzTest.extraWordsHeardOne": "{count} extra word heard that isn't in this ayah:",
  "hifzTest.extraWordsHeardOther": "{count} extra words heard that aren't in this ayah:",
  "memorize.playing": "Playing…",
  "memorize.playingLoopsLeft": "Playing… ({left} left)",
  "memorize.resultSummaryOne": "{count} word mistake · {missed} missed · {extra} extra",
  "memorize.resultSummaryOther": "{count} word mistakes · {missed} missed · {extra} extra",
  "memorize.attemptsOldestNewestOne": "{count} attempt · oldest → newest",
  "memorize.attemptsOldestNewestOther": "{count} attempts · oldest → newest",
  "memorize.markMemorizedContinue": "Mark memorized & continue",
  "memorize.markMemorizedFinish": "Mark memorized & finish",
  "achievements.xpProgress": "{xp} XP · {remaining} XP to Level {level}",
  "achievements.badgesEarned": "{earned}/{total} badges earned",
  "achievements.earnedOn": "Earned {date} ✓",
  "achievements.friendsJoinedOne": "{count} friend joined through you 🎉",
  "achievements.friendsJoinedOther": "{count} friends joined through you 🎉",
  "bookings.doneAccepted": "Booking accepted",
  "bookings.doneDeclined": "Booking declined",
  "bookings.doneCancelled": "Booking cancelled",
  "bookings.doneCompleted": "Booking marked completed",
  "bookings.doneUpdated": "Booking updated",
  "community.buddyMayBeStruggling": "{name} may be finding it hard lately — a kind word could change their week 🤲",
  "halaqa.studentsEnrolledOne": "{count} student enrolled",
  "halaqa.studentsEnrolledOther": "{count} students enrolled",
  "hifzPlan.ayahsDoneToday": "{done} of {goal} ayahs done today",
  "hifzPlan.moreAyahsToGoalOne": "{count} more ayah to hit today's goal.",
  "hifzPlan.moreAyahsToGoalOther": "{count} more ayahs to hit today's goal.",
  "mySubmissions.sentOn": "{className} · sent {date}",
  "mySubmissions.gradedBy": "{className} · graded by {teacher} on {date}",
  "mySubmissions.teacherDefault": "Teacher",
  "profile.setUpTwoFactor": "Set up two-factor authentication",
  "progress.revisionsLastOne": "{count} revision · {when}",
  "progress.revisionsLastOther": "{count} revisions · {when}",
  "progress.lastDate": "last {date}",
  "progress.neverRevised": "never revised",
  "progress.aiTeacher": "AI {ai} · Teacher {teacher}",
  "pulse.shareMessage": "📖 My week with the Qur'an on Hifz with Hikmah: {ayahs} new ayahs, {days}/7 active days, {streak}-day streak. Alhamdulillah! Memorize with understanding — join me:",
  "revision.scheduledAyahs": "Scheduled ayahs: {n}",
  "revision.queuedPrioritizedOne": "{count} ayah queued, prioritized for you: recovery first, then overdue, recent, and long-term.",
  "revision.queuedPrioritizedOther": "{count} ayahs queued, prioritized for you: recovery first, then overdue, recent, and long-term.",
  "revision.daysOverdueOne": "{count} day overdue",
  "revision.daysOverdueOther": "{count} days overdue",
  "revision.reviewsDoneOne": "{count} review done",
  "revision.reviewsDoneOther": "{count} reviews done",
  "revision.sessionDoneOne": "{count} ayah reviewed. Every schedule has been updated for you.",
  "revision.sessionDoneOther": "{count} ayahs reviewed. Every schedule has been updated for you.",
  "revision.reviewingOf": "Reviewing {n} of {total}",
  "surahView.tapAnAyahSwipe": "Tap an ayah to start memorizing it. Swipe the page to turn it.",
  "tafsirReader.sectionsToggle": "Sections",
  "teacherProfile.paymentSoon": "Online payment via Curlec is launching soon — for now, send the request and arrange payment with {name} directly.",
  "teachers.newRating": "New",
  "classReminderBanner.startingNow": "starting now",
  "classReminderBanner.inMinutes": "in {mins} min",
  "classReminderBanner.inHoursMinutes": "in {h}h {m}m",
  "classReminderBanner.halaqaStarting": "Halaqa starting {when}",
  "practiceMistakesDrill.practiceMistakesOne": "Practice {count} mistake",
  "practiceMistakesDrill.practiceMistakesOther": "Practice {count} mistakes",
  "practiceMistakesDrill.loopWord": "Loop {loopRound}/3 · Word {word}/{total}",
  "premiumUpgradeModal.saveAmount": "Save {amount}",
  "quranTree.ariaLabel": "Quran tree with {ayahs} leaves and {branches} branches",
  "streakRecoveryCard.hifzTokensThisWeekOne": "{count} Hifz Token this week",
  "streakRecoveryCard.hifzTokensThisWeekOther": "{count} Hifz Tokens this week",
  "streakRecoveryCard.tokensThisWeekOne": "{count} token this week",
  "streakRecoveryCard.tokensThisWeekOther": "{count} tokens this week",
  "submitToTeacher.submit": "Submit",
  "submitToTeacher.submitToTeacher": "Submit to teacher",
  "voiceRecorder.recording": "Recording {time}",

  "common.verseN": "Verse {n}",
  "common.versesRange": "Verses {start}–{end}",
  "teachers.goal.hifzTeacher": "Hifz Teacher",
  "teachers.goal.quranFluencyTeacher": "Quran Fluency Teacher",
  "teachers.goal.tajweedTeacher": "Tajweed Teacher",
  "teachers.goal.arabicTeacher": "Arabic Teacher",
  "teachers.goal.tafsirTeacher": "Tafsir Teacher",
  "teachers.goal.kidsSpecialist": "Kids Specialist",
  "teachers.goal.familyQuranCoach": "Family Quran Coach",
  "halaqa.scheduleTba": "Schedule TBA",
  "halaqa.liveNow": "Live now",
  "halaqa.startsInMinutes": "Starts in {mins} min",
  "halaqa.startsInHoursMinutes": "Starts in {h}h {m}m",
  "halaqa.startsInDaysHours": "Starts in {d}d {h}h",
  "hifzPlan.reviewsDueOne": "{count} review due",
  "hifzPlan.reviewsDueOther": "{count} reviews due",
  "hifzPlan.allCaughtUp": "All caught up",
  "hifzPlan.longTermCount": "{count} long-term",
  "legacy.treeSeed": "Your tree is a seed today. Memorize your first ayah and watch the first leaf appear, in shaa Allah.",
  "legacy.treeLeavesSoFarOne": "{count} leaf so far — your first branch grows at one full page.",
  "legacy.treeLeavesSoFarOther": "{count} leaves so far — your first branch grows at one full page.",
  "legacy.treeTakingRootOne": "{count} branch, {ayahs} leaves — your tree is taking root. Keep watering it daily.",
  "legacy.treeTakingRootOther": "{count} branches, {ayahs} leaves — your tree is taking root. Keep watering it daily.",
  "legacy.treeLegacy": "{branches} branches and {ayahs} leaves strong — a legacy your family will inherit, in shaa Allah.",
  "memorize.coachExcellent": "Excellent! Mashallah — that was nearly perfect.",
  "memorize.coachVeryCloseOne": "Very close — fix the {count} highlighted word and it's perfect.",
  "memorize.coachVeryCloseOther": "Very close — fix the {count} highlighted words and it's perfect.",
  "memorize.coachGreatJobOne": "Great job! Just polish {count} word and you're there.",
  "memorize.coachGreatJobOther": "Great job! Just polish {count} words and you're there.",
  "memorize.coachGoodEffort": "Good effort — repeat this ayah once more, focusing on the highlighted words.",
  "memorize.coachRebuild": "Let's rebuild it together: listen to the Qari three times, recite along, then try again.",
  "memorize.tipSkippedOne": "You skipped {count} word — read the ayah once with your eyes, then recite.",
  "memorize.tipSkippedOther": "You skipped {count} words — read the ayah once with your eyes, then recite.",
  "memorize.tipExtra": "You added extra words — slow down and let each word land.",
  "memorize.tipWrong": "Tap the highlighted words below and repeat each one three times.",
  "memorize.tipLowAccuracy": "Listen to the Qari once more, then record again. Repetition is the secret.",
  "memorize.tipKeepWarm": "Keep this ayah warm — recite it once in your next prayer.",
  "teacherProfile.impactStudents": "Students",
  "teacherProfile.impactHalaqasLed": "Halaqas led",
  "teacherProfile.impactFeedbackGiven": "Feedback given",
  "recorder.noAudioCaptured": "No audio captured. Check your mic and try again.",
  "recorder.micAccessDenied": "Microphone access denied. Allow mic in your browser.",
  "myQuran.searchFailed": "Search failed",
  "bookings.paidAmount": "Paid ${amount}",
  "bookings.amountDue": "${amount} due — online payment coming soon; settle directly for now",
  "bookings.freeTrial": "Free trial",
  "bookings.priceToBeAgreed": "Price to be agreed",
  "teacherNotificationBell.voiceNoteFrom": "Voice note from {name}",
  "teacherNotificationBell.voiceNoteFromTeacher": "Voice note from your teacher",
  "teacherNotificationBell.feedbackFrom": "Feedback from {name}",
  "teacherNotificationBell.feedbackFromTeacher": "Feedback from your teacher",
  "teacherNotificationBell.joinedNetwork": "{name} joined the Teachers Network",
  "teacherNotificationBell.tapToListen": "Tap to listen",
  "hifzTest.recitationScoringFailed": "Recitation scoring failed. Try again.",
  "hifzTest.couldnTFetchATip": "Couldn't fetch a tip right now — try again later.",
  "hifzTest.usedFreeMinutesAiTest": "You've used today's 5 free minutes of AI Test. Upgrade now to experience a month of continuous AI Test.",

  // ---- auto-extracted by the i18n codemod (do not renumber keys) ----
  "tadabur.tadabburTafsirExplorer": "Tadabbur · Tafsir Explorer",
  "tadabur.readTheQurAnLike": "Read the Qur'an like a book of meaning",
  "tadabur.eachSurahIsOrganizedInto": "Each surah is organized into thematic sections following recognized scholarly tafsir — understand the surah, then each theme, then memorize.",
  "tadabur.surahAyahs": "Surah {surah_number} · {total_ayahs} ayahs",
  "tadabur.makki": "Makki",
  "tadabur.madani": "Madani",
  "tadabur.thematicSections": "{section_count} thematic sections",
  "tadabur.ofPartsRead": "{done} of {total} parts read",
  "tadabur.continueReading": "Continue reading",
  "tadabur.startWithTheIntroduction": "Start with the introduction",
  "tadabur.all114SurahsAreComing": "All 114 surahs are coming",
  "tadabur.theExplorerStartsWithAl": "The Explorer starts with Al-Fatihah and Al-Baqarah. The same structure — introduction, revelation context, virtues, themes and grouped tafsir sections — will roll out to every surah.",
  "tadabur.organizationInspiredByScholarlyTafsir": "Organization inspired by scholarly tafsir structure · Grounded in Tafsir Ibn Kathir and As-Sa'di.",
  "myQuran.myQuran": "My Quran",
  "myQuran.readListenAndSearch": "Read, listen, and search",
  "myQuran.listen": "Listen",
  "myQuran.jumpToSurah": "Jump to surah",
  "myQuran.jumpToPage": "Jump to page",
  "myQuran.page": "Page {n}",
  "myQuran.jumpToJuz": "Jump to juz",
  "myQuran.juz": "Juz {j}",
  "myQuran.searchTheQuran": "Search the Quran…",
  "myQuran.clearSearch": "Clear search",
  "myQuran.nextPage": "Next page",
  "myQuran.previousPage": "Previous page",
  "myQuran.everythingForEngagingWithThe": "Everything for engaging with the Mushaf, in one place.",
  "myQuran.authenticKfgqpcV1PageFont": "Authentic KFGQPC v1 page font · loaded from quran.com CDN. Pages 50–604 rolling out based on student votes.",
  "myQuran.openTadabburForThisPage": "Open Tadabbur for this page",
  "myQuran.jumpToVerse": "Jump to verse",
  "myQuran.verse": "Verse",
  "myQuran.verse2": "Verse {n}",
  "myQuran.surahIsnTInThe": "Surah {name_en} isn't in the page reader yet — coming soon, in shaa Allah.",
  "myQuran.resultsFor": "Results for “{query}”",
  "myQuran.closeSearch": "Close search",
  "myQuran.searching": "Searching…",
  "myQuran.surah": "Surah {number} · {name_en}",
  "myQuran.surahAyah": "Surah {surah_number} : Ayah {verse_number}",
  "myQuran.noMatchesFor": "No matches for “{query}”.",
  "myQuran.resultsJumpThisPageThe": "Results jump this page — the mushaf stays open behind this panel.",
  "myQuran.pause": "Pause",
  "myQuran.play": "Play",
  "myQuran.closeListen": "Close listen",
  "myQuran.from": "From",
  "myQuran.to": "To",
  "myQuran.ayah": "Ayah {surah}:{currentAyah}",
  "myQuran.fewerOptions": "Fewer options ↑",
  "myQuran.moreOptions": "More options →",
  "myQuran.loop": "Loop:",
  "myQuran.speed": "Speed:",
  "myQuran.range": "· range {ayahStart}–{ayahEnd}",
  "myQuran.previousAyah": "Previous ayah",
  "myQuran.nextAyah": "Next ayah",
  "myQuran.loopRange": "Loop range:",
  "myQuran.openInHifzPlan": "Open in Hifz Plan",
  "myQuran.surahFilter": "Surah filter",
  "myQuran.all114Surahs": "All 114 surahs",
  "myQuran.clearFilter": "Clear filter",
  "myQuran.type2CharactersToBegin": "Type 2+ characters to begin searching",
  "myQuran.keepTypingAtLeast2": "Keep typing… at least 2 characters",
  "myQuran.inSurah": "in Surah {surah}",
  "myQuran.top50ByRelevance": "(top 50 by relevance)",
  "myQuran.ayahs": "{name_translation} · {total_ayahs} ayahs",
  "myQuran.tryAShorterQueryRemove": "Try a shorter query, remove diacritics, or clear the surah filter.",
  "madaniPage.madaniPageNotAvailableRight": "Madani page {pageNo} not available right now. Try again in a moment.",
  "madaniPage.hideReaderControls": "Hide reader controls",
  "madaniPage.showReaderControls": "Show reader controls",
  "madaniPage.surah": "Surah {surah}",
  "madaniPage.mushaf": "Mushaf",
  "madaniPage.chooseTheMushafScript": "Choose the Mushaf script",
  "madaniPage.switchToLightPage": "Switch to light page",
  "madaniPage.switchToDarkPage": "Switch to dark page",
  "madaniPage.togglePageTheme": "Toggle page theme",
  "madaniPage.page": "── Page {pageNo} ──",
  "madaniPage.inferredFromTheArabicText": "{description} — inferred from the Arabic text, not the Quran API's tajweed markup",
  "madaniPage.tapToOpenThisAyah": "Tap to open this ayah",
  "madaniPage.tapForTajweedTip": "Tap for tajweed tip",
  "madaniPage.word": "{verseKey} · word {position}",
  "hifzTest.welcomeToYourHifzDashboard": "Welcome to your hifz dashboard",
  "hifzTest.whatAreWeDoingToday": "What are we doing today?",
  "hifzTest.pickAFlowAssalamuAlaikum": "Pick a flow — assalamu alaikum, may Allah make it easy.",
  "hifzTest.surah": "Surah",
  "hifzTest.page": "Page",
  "hifzTest.reset": "Reset",
  "hifzTest.flow": "Flow",
  "hifzTest.recordingPlaybackUnavailableContinuing": "Recording playback unavailable — continuing without audio capture",
  "hifzTest.couldnTStartMicrophone": "Couldn't start microphone",
  "hifzTest.speechError": "Speech error: {code}",
  "hifzTest.liveAsrNotSupported": "Live ASR not supported",
  "hifzTest.yourBrowserDoesnTSupport": "Your browser doesn't support live speech recognition. Use Chrome, Edge, or Safari for the live mode, or switch to",
  "hifzTest.modeAbove": "mode above.",
  "hifzTest.selfTest": "Self test",
  "hifzTest.hiddenMushafLiveAsr": "Hidden mushaf · live ASR",
  "hifzTest.reciteAloudWordsRevealIn": "Recite aloud — words reveal in green as you say them correctly.",
  "hifzTest.resume": "Resume",
  "hifzTest.startReciting": "Start reciting",
  "hifzTest.stop": "Stop",
  "hifzTest.skipWord": "Skip word",
  "hifzTest.mistakeDetected": "Mistake detected",
  "hifzTest.expected": "Expected",
  "hifzTest.heard": "— heard",
  "hifzTest.tryAgainFromThisWord": "Try again from this word — or use",
  "hifzTest.toMoveOn": "to move on.",
  "hifzTest.skipWordNextAyah": "Skip word / Next ayah",
  "hifzTest.wordsRevealed": "{revealedIdx} / {wordListCount} words revealed",
  "hifzTest.skipped": "· {skipCount} skipped",
  "hifzTest.yourRecitation": "Your recitation",
  "hifzTest.mashallahRangeComplete": "Mashallah — range complete",
  "hifzTest.rangeFinishedReviewTheMarked": "Range finished — review the marked words",
  "hifzTest.wordsRight": "Words right:",
  "hifzTest.ofWordsNeedWork": "· {notRightCount} of {wordListCount} words need work",
  "hifzTest.sentToYourTeacherFor": "✓ Sent to your teacher for review.",
  "hifzTest.submittedToTeacher": "Submitted to teacher",
  "hifzTest.couldnTSubmitMakeSure": "Couldn't submit — make sure you've joined a halaqa first.",
  "hifzTest.sending": "Sending…",
  "hifzTest.sendToMyTeacher": "Send to my teacher",
  "hifzTest.backToSelfTestNo": "Back to self test (no mic)",
  "hifzTest.hiddenMushafSelfTest": "Hidden mushaf · self test",
  "hifzTest.allAyahsHiddenTap": "All ayahs hidden. Tap",
  "hifzTest.reveal": "Reveal",
  "hifzTest.orTapThe": "— or tap the",
  "hifzTest.toReciteItOutLoud": "to recite it out loud instead",
  "hifzTest.mic": "mic",
  "hifzTest.reciteAloud": "Recite aloud",
  "hifzTest.reciteThisRangeAloudRecording": "Recite this range aloud — recording starts immediately",
  "hifzTest.revealed": "Revealed:",
  "hifzTest.allRevealed": "All revealed",
  "hifzTest.finishTest": "Finish test",
  "hifzTest.testComplete": "Test complete",
  "hifzTest.perfect": "{rangeLabel} · perfect ({accuracy}%)",
  "hifzTest.aiScoringFailed": "AI scoring failed",
  "hifzTest.recordTheRangeFirstBefore": "Record the range first before submitting.",
  "hifzTest.youAlreadyHaveAPending": "You already have a pending submission for this surah",
  "hifzTest.sentToYourSelectedTeacher": "Sent to your selected teacher",
  "hifzTest.sentUnassignedPickAHalaqa": "Sent (unassigned — pick a halaqa to route to a teacher)",
  "hifzTest.couldnTSubmitTryAgain": "Couldn't submit — try again.",
  "hifzTest.aiRecitationFeedback": "AI recitation feedback",
  "hifzTest.reciteTheEntireRangeIn": "Recite the entire range in one go. Your AI coach scores Hifz + Tajweed and reveals every ayah at once. Submit to your teacher when you're happy.",
  "hifzTest.unlimited": "Unlimited",
  "hifzTest.5MinDayFree3": "5 min/day free · $3.99/mo for unlimited",
  "hifzTest.ayah": "Ayah {ayahStart}",
  "hifzTest.ayahs": "Ayahs {ayahStart}-{ayahEnd}",
  "hifzTest.rangeSentToYourTeacher": "✓ Range sent to your teacher for review.",
  "hifzTest.rangeCompleteSubmitToYour": "Range complete · submit to your teacher",
  "hifzTest.pickAHalaqaTeacher": "— Pick a halaqa / teacher —",
  "hifzTest.text": "{name} · {teacher_name}",
  "hifzTest.optionalNoteForYourTeacher": "Optional note for your teacher…",
  "hifzTest.youHavenTJoinedA": "You haven't joined a halaqa yet — submission will land in the \"Unassigned\" bucket.",
  "hifzTest.findAHalaqa": "Find a halaqa",
  "hifzTest.sendRangeToTeacher": "Send range to teacher",
  "hifzTest.theAiHeard": "· The AI heard",
  "hifzTest.thinking": "Thinking…",
  "hifzTest.mushafIsHidden": "Mushaf is hidden",
  "hifzTest.upgradeToAiPremiumTo": "Upgrade to AI Premium to keep testing today",
  "hifzTest.youVeUsedTodayS": "You've used today's 5 free minutes. Upgrade now to experience a month of continuous AI Test.",
  "hifzTest.upgradeNow": "Upgrade Now",
  "hifzTest.accuracy": "Accuracy",
  "hifzTest.wordsRight2": "Words right",
  "hifzTest.mistakes": "Mistakes",
  "hifzTest.whatTheAiHeard": "What the AI heard",
  "hifzTest.accuracyComparesLettersAcrossThe": "Accuracy compares letters across the whole ayah; Words right counts whole words. If the text above isn't what you said, record again closer to the mic in a quiet place — the AI can mishear.",
  "hifzTest.wordsToReviewTap": "Words to review · tap",
  "hifzTest.forATajweedTip": "for a tajweed tip",
  "hifzTest.why": "Why?",
  "hifzTest.autoAdvance": "Auto-advance ·",
  "hifzTest.ms": "{value}ms",
  "hifzTest.liveAsrWillMarkA": "Live ASR will mark a mistake and move forward automatically after this delay. Set to “Off” to require manual Skip.",
  "hifzTest.back": "Back",
  "memorize.xpEarned": "+{xp_gain} XP earned",
  "memorize.aiScoringFailedTryAgain": "AI scoring failed — try again",
  "memorize.ayahMemorizedMashallahRevisionSchedule": "Ayah memorized — mashallah! Revision scheduled.",
  "memorize.markedForMorePracticeYou": "Marked for more practice — you've got this.",
  "memorize.couldnTSave": "Couldn't save",
  "memorize.ayahOf": "Ayah {ayahNum} of {total}",
  "memorize.read": "Read",
  "memorize.listenRepeat": "Listen & Repeat",
  "memorize.repeat": "Repeat ×{n}",
  "memorize.listenUntilTheMelodySettles": "Listen until the melody settles in your ears — then recite along quietly.",
  "memorize.understand": "Understand",
  "memorize.loadingTafsir": "Loading tafsir…",
  "memorize.goDeeperInTheTadabbur": "Go deeper in the Tadabbur reader",
  "memorize.practiceMemorization": "Practice memorization",
  "memorize.reciteFromMemoryUnlimitedAttempts": "Recite from memory. Unlimited attempts — only your best matters.",
  "memorize.compareWithQari": "Compare with Qari",
  "memorize.listeningToYourRecitation": "Listening to your recitation…",
  "memorize.getAiFeedback": "Get AI feedback",
  "memorize.recordYourselfAboveFirst": "Record yourself above first.",
  "memorize.howToImprove": "How to improve",
  "memorize.recordAgain": "Record again",
  "memorize.yourProgressOnThisAyah": "Your progress on this ayah",
  "memorize.text": "{accuracy}%",
  "memorize.markMemorized": "Mark Memorized",
  "memorize.yourRecitationIsStrongLock": "Your recitation is strong — lock it in and it will join your revision cycle.",
  "memorize.aimFor85AccuracyBefore": "Aim for 85%+ accuracy before marking memorized, so revision stays light.",
  "memorize.needsMorePractice": "Needs more practice",
  "hifzPlan.myHifz": "My Hifz",
  "hifzPlan.yourPlanProgress": "Your plan & progress",
  "hifzPlan.todaySGoal": "Today's goal",
  "hifzPlan.loading": "Loading…",
  "hifzPlan.today": "Today",
  "hifzPlan.memorized": "Memorized",
  "hifzPlan.revisionDue": "Revision due",
  "hifzPlan.todaySGoalIsComplete": "Today's goal is complete — mashallah. Anything more is a bonus.",
  "hifzPlan.continueYourRevision": "Continue your revision",
  "hifzPlan.startAHifzTest": "Start a hifz test",
  "hifzPlan.aiFeedbackQuickPracticeOr": "AI feedback, quick practice or self test",
  "hifzPlan.thisSurahUnlocksSoonIn": "This surah unlocks soon, in shaa Allah",
  "hifzPlan.surah": "Surah {number} · {revelation}",
  "hifzPlan.comingSoon": "Coming soon",
  "hifzPlan.memorized2": "{memorized} memorized",
  "hifzPlan.learning": "{learning} learning",
  "hifzPlan.alFatihahFullAlBaqarah": "Al-Fatihah (full), Al-Baqarah (ayahs 1–10), Al-Kawthar, Al-Ikhlas, Al-Falaq, An-Nas — with Tadabbur, Audio Qari, and Recitation Testing enabled. More surahs unlock as you progress.",
  "hifzPlan.v1ContentScope": "v1 content scope:",
  "hifzPlan.freeScopeNote": "Basic Hifz is open to everyone: Al-Fatihah, Ayat al-Kursi, Amana ar-Rasul, the first and last 10 ayahs of Al-Kahf, Al-Mulk and the last 10 surahs. The steps above it open with a package and the matching course.",
  "hifzPlan.notOpenYet": "That surah isn't open on your plan yet. Here are your Hifz steps.",
  "hifzPlan.basicHifzCta": "Basic Hifz",
  "hifzPlan.basicHifzCtaSub": "{done} of {total} ayahs memorised · free for everyone",
  "hifzPlan.lockedPremium": "Not open yet",
  "basicHifz.eyebrow": "Free for everyone",
  "basicHifz.title": "Basic Hifz",
  "basicHifz.intro": "HikmahVerses doesn't ask everyone to become a hafiz. Every Muslim gets Basic Hifz of what they recite daily: these surahs and ayahs are free for your first year, with a self-test. Packages add Juz 'Amma; each later step opens only after the matching course. Every step ends with tadabbur: a short reflection and one thing to apply in daily life.",
  "basicHifz.progress": "{done} of {total} ayahs memorised",
  "basicHifz.groupDaily": "Recited daily",
  "basicHifz.groupLast10": "The last 10 surahs",
  "basicHifz.why": "Why it is in Basic Hifz",
  "basicHifz.ayahOne": "Ayah {ranges}",
  "basicHifz.ayahsMany": "Ayahs {ranges}",
  "basicHifz.start": "Start",
  "basicHifz.continue": "Continue",
  "basicHifz.memorised": "Memorised",
  "basicHifz.ofTotal": "{done}/{total}",
  "basicHifz.aiTitle": "AI test allowance",
  "basicHifz.aiBody": "10 AI tests a month in every package, and for each Dar as-Salam member. Each test runs up to {minutes} minutes and counts as one, however short. Basic Hifz in Noor has the non-AI self-test.",
  "basicHifz.stepsTitle": "Your Hifz steps",
  "basicHifz.stepOpen": "Open",
  "basicHifz.stepLocked": "Locked",
  "basicHifz.afterCourses": "Opens after Course {n}",
  "basicHifz.afterCoursesMany": "Opens after Courses 1-{n}",
  "basicHifz.needsPackage": "Needs a package",
  "basicHifz.needsIrtaqi": "Needs the Irtaqi add-on",
  "basicHifz.coursesDone": "Understand Qur'an courses finished: {done}",
  "basicHifz.openCourses": "Open Understand Qur'an",
  "basicHifz.aiLeft": "{left} of {total} left this month",
  "basicHifz.testCta": "Take a Hifz test",
  "basicHifz.upgradeTitle": "Want to memorise more?",
  "basicHifz.upgradeBody": "A package adds Juz 'Amma and 10 AI tests a month; the Irtaqi add-on opens the later steps.",
  "basicHifz.seePlans": "See plans",
  "basicHifz.moreLocked": "{count} more ayahs of this surah open with a later Hifz step.",
  "basicHifz.seeSteps": "See Hifz steps",
  "basicHifz.loadFailed": "Couldn't load Basic Hifz. Please try again.",
  "surahView.couldnTLoadSurah": "Couldn't load surah",
  "surahView.fetchingSurahFromTheMushaf": "Fetching surah from the Mushaf…",
  "surahView.allSurahs": "All surahs",
  "surahView.page": "· Page {page}",
  "surahView.ayah": "Ayah {n}",
  "tafsirReader.thisSurahIsnTIn": "This surah isn't in the Tafsir Explorer yet",
  "tafsirReader.couldnTLoadThisSection": "Couldn't load this section",
  "tafsirReader.introduction": "Introduction",
  "tafsirReader.searchThemesTopicsVerses": "Search themes, topics, verses…",
  "tafsirReader.markIntroductionAsNotRead": "Mark introduction as not read",
  "tafsirReader.markIntroductionAsRead": "Mark introduction as read",
  "tafsirReader.text": "{start}–{end}",
  "tafsirReader.markAsNotRead": "Mark \"{title}\" as not read",
  "tafsirReader.markAsRead": "Mark \"{title}\" as read",
  "tafsirReader.noSectionsMatch": "No sections match \"{query}\".",
  "tafsirReader.preparingThisSection": "Preparing this section…",
  "tafsirReader.theFirstReaderOfEach": "The first reader of each section waits a moment while its tafsir is composed and grounded in Ibn Kathir — after that it opens instantly for everyone.",
  "tafsirReader.previousSection": "Previous section",
  "tafsirReader.nextSection": "Next section",
  "tafsirReader.startMemorizing": "Start memorizing",
  "tafsirReader.surahIntroduction": "Surah {surah_number} · Introduction",
  "tafsirReader.ayahs": "\"{meaning}\" · {total_ayahs} ayahs",
  "tafsirReader.revelation": "Revelation",
  "tafsirReader.historicalBackground": "Historical Background",
  "tafsirReader.virtues": "Virtues",
  "tafsirReader.mainThemes": "Main Themes",
  "tafsirReader.learningObjectives": "Learning Objectives",
  "tafsirReader.byTheEndOfThis": "By the end of this surah you will…",
  "tafsirReader.startReading": "Start reading",
  "tafsirReader.moreSectionsFollowOpenThe": "More sections follow — open the next one",
  "tafsirReader.goToTheNextSection": "Go to the next section",
  "tafsirReader.bookmarked": "Bookmarked",
  "tafsirReader.bookmarkThisSection": "Bookmark this section",
  "tafsirReader.removeBookmark": "Remove bookmark",
  "tafsirReader.listening": "Listening…",
  "tafsirReader.listenToThisSection": "Listen to this section",
  "tafsirReader.ayahOf": "Ayah {currentAyah} of {start}–{end}",
  "tafsirReader.explanation": "Explanation",
  "tafsirReader.keyLessons": "Key Lessons",
  "tafsirReader.vocabulary": "Vocabulary",
  "tafsirReader.reflection": "Reflection",
  "tafsirReader.actionPoints": "Action points",
  "tafsirReader.todaySTakeaway": "Today's Takeaway",
  "tafsirReader.allahSNames": "Allah's Names",
  "tafsirReader.seerahConnection": "Seerah Connection",
  "tafsirReader.dua": "Dua",
  "tafsirReader.do": "Do",
  "tafsirReader.donT": "Don't",
  "tafsirReader.lifeApplication": "Life Application",
  "tafsirReader.howCanIPracticeThis": "How can I practice this today?",
  "tafsirReader.openMyNotes": "Open My Notes",
  "tafsirReader.myNotes": "My Notes",
  "tafsirReader.whatMovedYouInThis": "What moved you in this section? Write it down…",
  "tafsirReader.saveNote": "Save note",
  "tafsirReader.noteSaved": "Note saved",
  "tafsirReader.references": "References",
  "tafsirReader.primary": "Primary",
  "tafsirReader.supporting": "Supporting",
  "tafsirReader.theArchitectureSupportsMultipleTafsir": "The architecture supports multiple tafsir sources — source switching arrives in a future version.",
  "revision.memoryStrength5": "Memory strength {value}/5",
  "revision.couldnTSaveReview": "Couldn't save review",
  "revision.aiHifzCoach": "AI Hifz Coach",
  "revision.revision": "Revision",
  "revision.todaySRevision": "Today's revision",
  "revision.allCaughtUpNothingDue": "All caught up — nothing due today. Mashallah!",
  "revision.startTodaySRevision": "Start Today's Revision",
  "revision.queue": "Queue",
  "revision.ayah": "{surah_name} · Ayah {ayah}",
  "revision.aiTest": "AI test",
  "revision.revisionComplete": "Revision complete!",
  "revision.backToQueue": "Back to queue",
  "revision.exitSession": "Exit session",
  "revision.reciteThisAyahFromMemory": "Recite this ayah from memory",
  "revision.takeYourTimeReciteOut": "Take your time. Recite out loud, then reveal to check yourself.",
  "revision.revealAyah": "Reveal ayah",
  "revision.howDidItGo": "How did it go?",
  "revision.orTakeTheAiRecitation": "Or take the AI recitation test for this ayah →",
  "revision.dailyGoalUpdatedAboutAyahs": "Daily goal updated — about {daily_ayahs} ayahs a day. Your plan has been recalculated.",
  "revision.couldnTUpdateGoal": "Couldn't update goal",
  "revision.dailyGoal": "Daily Goal",
  "revision.currentlyAbout": "Currently about",
  "revision.ofNewMemorizationPerDay": "of new memorization per day. Change it anytime — no progress is ever lost.",
  "revision.ayahs": "{daily_ayahs} ayahs",
  "revision.coachRecommendsDay": "Coach recommends: {recLabel} / day",
  "revision.acceptRecommendation": "Accept recommendation",
  "revision.customAyahs": "Custom ayahs…",
  "revision.set": "Set",
  "achievements.motivation": "Motivation",
  "achievements.achievements": "Achievements",
  "achievements.everyBadgeMarksAMilestone": "Every badge marks a milestone on your journey with the Qur'an.",
  "achievements.level": "Level {level}",
  "achievements.share": "Share",
  "achievements.alhamdulillahIJustEarnedThe": "🏆 Alhamdulillah! I just earned the \"{title}\" badge on Hifz with Hikmah — memorizing the Qur'an with understanding. Join me on the journey!",
  "achievements.inviteFriends": "Invite friends",
  "achievements.shareYourPersonalLinkEvery": "Share your personal link — every friend who joins is counted for you.",
  "achievements.beTheFirstInYour": "Be the first in your halaqa to invite a friend!",
  "achievements.copied": "Copied",
  "achievements.copy": "Copy",
  "achievements.inviteLinkCopied": "Invite link copied!",
  "progress.learningTracker": "Learning Tracker",
  "progress.myProgress": "My Progress",
  "progress.timeline": "Timeline",
  "progress.cards": "Cards",
  "progress.table": "Table",
  "progress.search": "Search…",
  "progress.allStatuses": "All statuses",
  "progress.learning": "Learning",
  "progress.revising": "Revising",
  "progress.surah": "Surah {n}",
  "progress.noTrackedAyahsYet": "No tracked ayahs yet",
  "progress.startMemorizingAndYourProgress": "Start memorizing and your progress will appear here.",
  "progress.noActivityMatchesYourFilters": "No activity matches your filters yet.",
  "progress.inRecovery": "In recovery",
  "progress.text": "{ai_score}%",
  "progress.noReviewsYet": "No reviews yet",
  "progress.recovery": "RECOVERY",
  "halaqa.couldnTLoadHalaqas": "Couldn't load halaqas",
  "halaqa.joinedTheHalaqa": "Joined the halaqa",
  "halaqa.couldnTJoin": "Couldn't join",
  "halaqa.attendanceMarked": "Attendance marked",
  "halaqa.joiningZoom": "Joining Zoom…",
  "halaqa.halaqaCreated": "Halaqa created",
  "halaqa.invalidForm": "Invalid form",
  "halaqa.halaqa": "Halaqa",
  "halaqa.classesCohorts": "Classes & cohorts",
  "halaqa.joinATeacherLedHalaqa": "Join a teacher-led halaqa. Memorize together. Get reviewed. Zoom links + auto-reminders included.",
  "halaqa.cancel": "Cancel",
  "halaqa.newHalaqa": "New halaqa",
  "halaqa.halaqaName": "Halaqa name",
  "halaqa.shortDescription": "Short description",
  "halaqa.recurringSchedule": "Recurring schedule",
  "halaqa.time": "Time",
  "halaqa.timezone": "Timezone",
  "halaqa.asiaKualaLumpur": "Asia/Kuala_Lumpur",
  "halaqa.zoomOptional": "Zoom (optional)",
  "halaqa.passcode": "Passcode",
  "halaqa.passcodeIsHiddenFromStudents": "Passcode is hidden from students until 60 minutes before each session.",
  "halaqa.createHalaqa": "Create halaqa",
  "halaqa.noHalaqasYet": "No halaqas yet.",
  "halaqa.soon": "Soon",
  "halaqa.flexible": "Flexible",
  "halaqa.teacher": "Teacher ·",
  "halaqa.tba": "TBA",
  "halaqa.joinHalaqa": "Join halaqa",
  "halaqa.enrolled": "Enrolled",
  "halaqa.joinZoom": "Join Zoom",
  "halaqa.available30MinBeforeStart": "Available 30 min before start",
  "community.sent": "Sent \"{message}\" 🤲",
  "community.community": "Community",
  "community.youNeverMemorizeAlone": "You never memorize alone",
  "community.myQuranCompanion": "My Quran Companion",
  "community.hasnTStudiedForDays": "Hasn't studied for {days_quiet} days — send them a dua 🤲",
  "community.dayStreakWalkingTheJourney": "{streak}-day streak · walking the journey with you",
  "community.pickOneCompanionToWalk": "Pick one companion to walk the journey with. Share your code:",
  "community.copyCode": "Copy code",
  "community.codeCopied": "Code copied",
  "community.enterAFriendSCode": "Enter a friend's code",
  "community.myCircles": "My Circles",
  "community.joinWithCode": "Join with code",
  "community.newCircle": "New circle",
  "community.smallCirclesFamilyFriendsHalaqa": "Small circles — family, friends, halaqa — keep each other going. Create your first one.",
  "community.today": "{name} — today",
  "community.close": "Close",
  "community.theUmmahToday": "The Ummah today",
  "community.theFirstInspiringMomentWill": "The first inspiring moment will appear here soon, in shaa Allah.",
  "community.joinACircle": "Join a circle",
  "community.becomeCompanions": "Become companions",
  "community.circleNameEGThe": "Circle name (e.g. The Rahman Family)",
  "community.create": "Create",
  "community.circleCreatedInviteCode": "Circle created — invite code: {code}",
  "community.enterInviteCode": "Enter invite code",
  "community.join": "Join",
  "profile.deleteAccount": "Delete my account",
  "profile.deleteAccountBody": "Permanently remove your HikmahVerses account and your progress in the apps. You will confirm on your HikmahVerses profile page. This cannot be undone.",
  "profile.deleteAccountButton": "Delete my account…",
  "community.youAndAreNowQuran": "You and {buddy_name} are now Quran Companions 🤝",
  "community.requestSentTo": "Request sent to {buddy_name}. You become companions when they accept.",
  "community.wantsToBeYourCompanion": "{name} would like to be your Quran companion",
  "community.accept": "Accept",
  "community.decline": "Decline",
  "community.waitingForReply": "Waiting for {name} to accept",
  "community.cancelRequest": "Cancel request",
  "community.endCompanionship": "End companionship",
  "community.companionshipEnded": "Companionship ended",
  "community.joinedWelcome": "Joined \"{name}\" — welcome!",
  "legacy.quranLegacy": "Quran Legacy",
  "legacy.yourQuranTree": "Your Quran Tree",
  "legacy.everyAyahAddsALeaf": "Every ayah adds a leaf. Every page grows a branch. A living legacy.",
  "legacy.leavesAyahs": "Leaves (ayahs)",
  "legacy.branchesPages": "Branches (pages)",
  "legacy.hoursWithTheQuran": "Hours with the Quran",
  "legacy.shareMyQuranTree": "Share my Quran Tree",
  "legacy.familyLegacy": "Family Legacy",
  "legacy.aFamilyThatMemorizesTogether": "A family that memorizes together builds one shared legacy. Create a family circle to grow a tree together.",
  "legacy.createAFamilyCircle": "Create a family circle",
  "legacy.studiedToday": "{active_today}/{member_count} studied today",
  "legacy.ayahsTogether": "Ayahs together",
  "legacy.pagesTogether": "Pages together",
  "legacy.hoursTogether": "Hours together",
  "legacy.bestStreak": "Best streak",
  "legacy.ayahsH": "{ayahs} ayahs · {hours}h",
  "pulse.couldnTLoadPulse": "Couldn't load pulse",
  "pulse.parentShareLinkCreatedValid": "Parent share link created · valid 14 days",
  "pulse.couldnTCreateShareLink": "Couldn't create share link",
  "pulse.loadingYourWeek": "Loading your week…",
  "pulse.aWeeklySnapshotShareWith": "A weekly snapshot — share with your parent or teacher.",
  "pulse.shareMyWeek": "Share my week",
  "pulse.creating": "Creating…",
  "pulse.shareWithParent": "Share with parent",
  "profile.profileUpdated": "Profile updated",
  "profile.couldnTSaveProfile": "Couldn't save profile",
  "profile.yourInformation": "Your information",
  "profile.name": "Name",
  "profile.gender": "Gender",
  "profile.select": "— Select —",
  "profile.city": "City",
  "profile.country": "Country",
  "profile.ageGroup": "Age group",
  "profile.whatsappNumber": "WhatsApp number",
  "profile.saving": "Saving…",
  "profile.saveChanges": "Save changes",
  "profile.newPasswordMustBeAt": "New password must be at least 6 characters",
  "profile.newPasswordsDonTMatch": "New passwords don't match",
  "profile.passwordChanged": "Password changed",
  "profile.couldnTChangePassword": "Couldn't change password",
  "profile.changePassword": "Change password",
  "profile.currentPassword": "Current password",
  "profile.newPassword": "New password",
  "profile.confirmNewPassword": "Confirm new password",
  "profile.updating": "Updating…",
  "profile.updatePassword": "Update password",
  "profile.couldnTStartMfaSetup": "Couldn't start MFA setup",
  "profile.mfaEnabledYourAccountIs": "MFA enabled — your account is now protected by an authenticator app",
  "profile.invalidCode": "Invalid code",
  "profile.mfaDisabled": "MFA disabled",
  "profile.couldnTDisableMfa": "Couldn't disable MFA",
  "profile.twoFactorAuthentication": "Two-factor authentication",
  "profile.protectYourAccountWithGoogle": "Protect your account with Google Authenticator, Microsoft Authenticator, or any compatible app.",
  "profile.enabled": "Enabled",
  "profile.disableMfa": "Disable MFA",
  "profile.confirmYourPassword": "Confirm your password",
  "profile.disabling": "Disabling…",
  "profile.confirmDisable": "Confirm disable",
  "profile.scanThisQrCodeWith": "Scan this QR code with your authenticator app, then enter the 6-digit code it shows.",
  "profile.mfaQrCode": "MFA QR code",
  "profile.canTScanEnterManually": "Can't scan? Enter manually:",
  "profile.verifying": "Verifying…",
  "profile.enableMfa": "Enable MFA",
  "profile.streak": "Streak",
  "profile.ayahs": "Ayahs",
  "profile.xp": "XP",
  "profile.signOut": "Sign out",
  "profile.yourJourneyYourSettings": "Your journey, your settings",
  "teachers.matchingFailedTryAgain": "Matching failed — try again",
  "teachers.findMyPerfectMatch": "Find my perfect match",
  "teachers.whoIsLearning": "Who is learning?",
  "teachers.children": "Children",
  "teachers.teenagers": "Teenagers",
  "teachers.adults": "Adults",
  "teachers.families": "Families",
  "teachers.learningGoal": "Learning goal",
  "teachers.preferredLanguage": "Preferred language",
  "teachers.eGEnglish": "e.g. English",
  "teachers.preferredTeacherGender": "Preferred teacher gender",
  "teachers.noPreference": "No preference",
  "teachers.male": "Male",
  "teachers.female": "Female",
  "teachers.yourCountryOptional": "Your country (optional)",
  "teachers.matching": "Matching…",
  "teachers.showMyTop5Matches": "Show my top 5 matches",
  "teachers.noPublicTeachersToMatch": "No public teachers to match yet — check back soon, in shaa Allah.",
  "teachers.match": "match",
  "teachers.adjustAnswers": "Adjust answers",
  "teachers.globalQuranTeachersNetwork": "Global Quran Teachers Network",
  "teachers.findYourMentor": "Find your mentor",
  "teachers.verifiedQualifiedEducatorsChosenFor": "Verified, qualified educators — chosen for trust and impact, not just ratings.",
  "teachers.findMyMatch": "Find my match",
  "teachers.myBookings": "My bookings",
  "teachers.voiceNotes": "Voice Notes",
  "teachers.quranTeacher": "Quran teacher?",
  "teachers.getVerified": "Get Verified",
  "teachers.searchByNameCountryInstitution": "Search by name, country, institution or specialty…",
  "teachers.language": "Language",
  "teachers.category": "Category",
  "teachers.verification": "Verification",
  "teachers.mode": "Mode",
  "teachers.online": "Online",
  "teachers.offline": "Offline",
  "teachers.hybrid": "Hybrid",
  "teachers.clearFilters": "Clear filters",
  "teachers.noVerifiedTeachersMatchThese": "No verified teachers match these filters yet. The network is growing, in shaa Allah.",
  "teachers.featured": "Featured",
  "teachers.yExp": "{years_teaching}y exp",
  "teachers.ai": "🎙 AI {recitation_overall}",
  "teacherProfile.thisTeacherProfileIsnT": "This teacher profile isn't public yet.",
  "teacherProfile.backToDirectory": "Back to directory",
  "teacherProfile.requestSentToTheyVe": "Request sent to {name} — they've been emailed. You'll see their reply in My Bookings, in shaa Allah",
  "teacherProfile.couldnTSendRequest": "Couldn't send request",
  "teacherProfile.jazakallahKhairForYourReview": "JazakAllah khair for your review",
  "teacherProfile.couldnTSubmitReview": "Couldn't submit review",
  "teacherProfile.allTeachers": "All teachers",
  "teacherProfile.years": "{years_teaching} years",
  "teacherProfile.bookTrialLesson": "Book Trial Lesson",
  "teacherProfile.messageTeacher": "Message Teacher",
  "teacherProfile.directMessagingOpensInThe": "Direct messaging opens in the next release, in shaa Allah",
  "teacherProfile.requestALessonWith": "Request a lesson with {name}",
  "teacherProfile.sessionType": "Session type",
  "teacherProfile.trialClassFreeIntro": "Trial class (free intro)",
  "teacherProfile.oneToOneLesson": "One-to-one lesson",
  "teacherProfile.familySession": "Family session",
  "teacherProfile.weeklyPlan": "Weekly plan",
  "teacherProfile.monthlyPlan": "Monthly plan",
  "teacherProfile.preferredTime": "Preferred time",
  "teacherProfile.noteToTeacher": "Note to teacher",
  "teacherProfile.yourGoalsLevelAnythingHelpful": "Your goals, level, anything helpful…",
  "teacherProfile.billedAt": "Billed at",
  "teacherProfile.sendRequest": "Send request",
  "teacherProfile.about": "About",
  "teacherProfile.hearSeeTheTeaching": "Hear & see the teaching",
  "teacherProfile.sampleRecitation": "Sample recitation — {surah_name} {ayah_start}–{ayah_end}",
  "teacherProfile.teachingDemonstration": "Teaching demonstration",
  "teacherProfile.impactOnThePlatform": "Impact on the platform",
  "teacherProfile.reviews": "Reviews",
  "teacherProfile.noReviewsYetBeThe": "No reviews yet — be the first to share your experience.",
  "teacherProfile.shareYourExperienceWithThis": "Share your experience with this teacher…",
  "teacherProfile.submitReview": "Submit review",
  "bookings.with": "with {teacher_name}",
  "bookings.from": "from {student_name}",
  "bookings.timeToBeAgreed": "Time to be agreed",
  "bookings.text": "· “{note}”",
  "bookings.joinSession": "Join session",
  "bookings.joinLinkZoomMeetOptional": "Join link (Zoom/Meet) — optional",
  "bookings.accept": "Accept",
  "bookings.decline": "Decline",
  "bookings.markCompleted": "Mark completed",
  "bookings.actionFailed": "Action failed",
  "bookings.teachers": "Teachers",
  "bookings.teachersNetwork": "Teachers Network",
  "bookings.myBookings": "My Bookings",
  "bookings.requestsFromStudents": "Requests from students",
  "bookings.myLessonRequests": "My lesson requests",
  "bookings.noLessonRequestsYetFind": "No lesson requests yet. Find a mentor and book a trial class.",
  "bookings.browseTeachers": "Browse teachers",
  "leaderboard.leaderboard": "Leaderboard",
  "leaderboard.topStudents": "Top students",
  "leaderboard.friendlyCompetitionKeepYourStreak": "Friendly competition — keep your streak alive.",
  "leaderboard.student": "Student",
  "leaderboard.noStudentsYet": "No students yet.",
  "leaderboard.showMeTitle": "Show me on the leaderboard",
  "leaderboard.showMeBody": "Nobody is listed unless they choose to be. Only the name you type here is shown, never your account name, email or picture.",
  "leaderboard.displayName": "Display name",
  "leaderboard.save": "Save",
  "leaderboard.hideMe": "Hide me",
  "leaderboard.saved": "Saved",
  "leaderboard.hidden": "You are no longer on the leaderboard",
  "leaderboard.youAreListed": "You appear as {name}",
  "voiceNotes.couldnTLoadVoiceNotes": "Couldn't load voice notes",
  "voiceNotes.fromYourTeacher": "From your teacher",
  "voiceNotes.voiceNotes": "Voice notes",
  "voiceNotes.personalVoiceFeedbackFromYour": "Personal voice feedback from your teacher — listen, learn, and act on it.",
  "voiceNotes.noVoiceNotesYetYour": "No voice notes yet. Your teacher will send them here.",
  "mySubmissions.couldnTLoadYourSubmissions": "Couldn't load your submissions",
  "mySubmissions.loadingYourSubmissions": "Loading your submissions…",
  "mySubmissions.mySubmissions": "My submissions",
  "mySubmissions.sentToMyTeacher": "Sent to my teacher",
  "mySubmissions.recitationsYouSubmittedForGrading": "Recitations you submitted for grading.",
  "mySubmissions.pendingReview": "Pending review · {pendingCount}",
  "mySubmissions.nothingPending": "Nothing pending.",
  "mySubmissions.surahAyah": "Surah {surah} · Ayah {ayah}",
  "mySubmissions.awaitingTeacher": "Awaiting teacher",
  "mySubmissions.aiAccuracy": "AI accuracy:",
  "mySubmissions.aiTajweed": "AI tajweed:",
  "mySubmissions.graded": "Graded · {gradedCount}",
  "mySubmissions.noGradedRecitationsYet": "No graded recitations yet.",
  "mySubmissions.teacherSNote": "Teacher's note",
  "continueJourney.takingYouBackToWhere": "Taking you back to where you stopped…",
  "mushafView.madaniMushaf": "Madani Mushaf",
  "mushafView.authentic15LineKfgqpcLayout": "Authentic 15-line KFGQPC layout · Al-Faatihah + Al-Baqarah (49 pages)",
  "mushafView.tadabbur": "Tadabbur",
  "mushafView.prev": "Prev",
  "mushafView.of": "of {MAX_PAGE}",
  "mushafView.next": "Next",
  "premiumUpgradeModal.paymentsAreComingSoonCheck": "Payments are coming soon — check back shortly!",
  "premiumUpgradeModal.aiPremiumIsActiveEnjoy": "AI Premium is active — enjoy unlimited daily practice!",
  "premiumUpgradeModal.paymentWentThroughButActivation": "Payment went through but activation failed — contact support.",
  "premiumUpgradeModal.couldnTStartCheckoutTry": "Couldn't start checkout — try again shortly.",
  "premiumUpgradeModal.aiPremium": "AI Premium",
  "premiumUpgradeModal.youVeUsedTodayS": "You've used today's 5 free minutes of AI Test — upgrade now to experience a month of continuous AI Test.",
  "premiumUpgradeModal.continuousAiTestNoDaily": "Continuous AI Test — no daily 5-minute limit while active",
  "premiumUpgradeModal.freePlanStaysAt5": "Free plan stays at 5 minutes/day if you'd rather not upgrade",
  "premiumUpgradeModal.renewsOnlyIfYouUpgrade": "Renews only if you upgrade again — no auto-lock-in surprises",
  "premiumUpgradeModal.openingCheckout": "Opening checkout…",
  "premiumUpgradeModal.secureCheckoutViaCurlec": "Secure checkout via Curlec.",
  "premiumUpgradeModal.paymentsLaunchingSoon": "Payments launching soon.",
  "streakRecoveryCard.streakRestoredToDaysMashallah": "Streak restored to {streak} days · Mashallah",
  "streakRecoveryCard.couldnTRecoverStreak": "Couldn't recover streak",
  "streakRecoveryCard.streakBreakDetected": "Streak break detected",
  "streakRecoveryCard.restoreYourDayStreak": "Restore your {pre_break_streak}-day streak",
  "streakRecoveryCard.completeAyahsTodayAndSpend": "Complete {recovery_required} ayahs today and spend 1 Hifz Token to bring your streak back.",
  "streakRecoveryCard.todaySRecoveryProgress": "Today's recovery progress",
  "streakRecoveryCard.restoring": "Restoring…",
  "streakRecoveryCard.recoverStreak": "Recover streak",
  "streakRecoveryCard.keepGoing": "Keep going…",
  "streakRecoveryCard.hifzTokens": "Hifz Tokens",
  "streakRecoveryCard.tokensRefillEveryMondayIf": "Tokens refill every Monday. If you miss a day, spend one and complete 6 ayahs to restore your streak.",
  "streakBadge.dayStreak": "{streak} day streak",
  "careCard.todaySPlanIsNow": "Today's plan is now lighter — one gentle step at a time 🤲",
  "careCard.couldnTUpdateYourPlan": "Couldn't update your plan — please try again",
  "careCard.aGentleCheckInFrom": "A gentle check-in from your coach",
  "careCard.takeTodaySLighterPlan": "Take today's lighter plan",
  "careCard.iMOkayKeepMy": "I'm okay — keep my plan",
  "familyMessages.from": "From {from_name}",
  "familyMessages.voiceMessage": "Voice message",
  "familyMessages.fromYourFamily": "From your family",
  "familyMessages.personalEncouragementFromThoseWho": "Personal encouragement from those who love you.",
  "hadithOfDayBadge.fromTheSunnahForYou": "From the Sunnah — for you today",
  "classReminderBanner.attendanceMarkedSeeYouIn": "Attendance marked — see you in class!",
  "classReminderBanner.with": "· with {teacher_name}",
  "classReminderBanner.passcode": "· Passcode",
  "classReminderBanner.noZoomLinkYet": "No Zoom link yet",
  "classReminderBanner.dismiss": "Dismiss",
  "submitToTeacher.joinAHalaqaFirstTo": "Join a halaqa first to submit for grading",
  "submitToTeacher.alreadySubmittedWaitingOnTeacher": "Already submitted — waiting on teacher",
  "submitToTeacher.submittedToYourTeacherMashallah": "Submitted to your teacher · Mashallah",
  "submitToTeacher.couldnTSubmit": "Couldn't submit",
  "submitToTeacher.submitted": "Submitted",
  "submitToTeacher.joinAHalaqaToSubmit": "Join a halaqa to submit for grading",
  "submitToTeacher.sendForGrading": "Send for grading",
  "bookmarkIcon.bookmarks": "{itemsCount} bookmarks",
  "bookmarkIcon.bookmarks2": "Bookmarks",
  "bookmarkIcon.noBookmarksYetTapBookmark": "No bookmarks yet — tap Bookmark on a Tadabbur section to save it here.",
  "teacherNotificationBell.unreadTeacherNotifications": "{unread} unread teacher notifications",
  "teacherNotificationBell.teacherNotifications": "Teacher notifications",
  "teacherNotificationBell.fromYourTeachers": "From your teachers",
  "teacherNotificationBell.browse": "Browse →",
  "teacherNotificationBell.noTeacherMessagesYetJoin": "No teacher messages yet — join a halaqa or book a lesson to get personal guidance.",
  "practiceMistakesDrill.mashallahNoMistakesToDrill": "Mashallah — no mistakes to drill on this attempt.",
  "practiceMistakesDrill.drillTheTrickyBits": "Drill the tricky bits",
  "practiceMistakesDrill.tapAnyWordToHear": "Tap any word to hear the correct pronunciation. Adjust speed and let auto-loop drill all mistakes 3× for you.",
  "practiceMistakesDrill.speed": "Speed",
  "practiceMistakesDrill.stopAutoLoop": "Stop auto-loop",
  "practiceMistakesDrill.autoLoop3": "Auto-loop 3×",
  "practiceMistakesDrill.wordAudioViaQuranCom": "Word audio via Quran.com CDN · pronunciation drills are recommended slow (0.5–0.75×) then up to natural pace.",
  "recorder.recordingTapToStop": "Recording — tap to stop",
  "recorder.tapToStartReciting": "Tap to start reciting",
  "recorder.tapTheMicToBegin": "Tap the mic to begin your recitation.",
  "recorder.recording": "Recording…",
  "recorder.reciteTheAyahClearly": "— recite the ayah clearly.",
  "recorder.recorded": "Recorded",
  "recorder.tapTheMicToRecord": "— tap the mic to record again.",
  "voiceRecorder.couldnTAccessMicrophone": "Couldn't access microphone",
  "voiceRecorder.recordVoiceNote": "Record voice note",
  "voiceRecorder.send": "Send",
  "installAppButton.downloadApp": "Download App",
  "installAppButton.installHifzWithHikmah": "Install Hifz with Hikmah",
  "installAppButton.addThisAppToYour": "Add this app to your Home Screen for a full-screen, app-like experience.",
  "installAppButton.addThisAppToYour2": "Add this app to your device for quick, full-screen access — no app store needed.",
  "installAppButton.tapThe": "Tap the",
  "installAppButton.shareIconInSafariS": "Share icon in Safari's toolbar.",
  "installAppButton.scrollDownAndTap": "Scroll down and tap",
  "installAppButton.addToHomeScreen": "Add to Home Screen",
  "installAppButton.tap": "Tap",
  "installAppButton.toConfirm": "to confirm.",
  "installAppButton.add": "Add",
  "installAppButton.lookForAnInstallIcon": "Look for an install icon at the right end of your browser's address bar and click it.",
  "installAppButton.donTSeeOneOpen": "Don't see one? Open your browser's menu (",
  "installAppButton.andLookFor": ") and look for",
  "installAppButton.or": "or",
  "installAppButton.installApp": "\"Install app\"",
  "installAppButton.addToHomeScreen2": "\"Add to Home screen\"",
  "installAppButton.worksBestInChromeEdge": "Works best in Chrome, Edge, or Safari.",
  "nextStepCard.go": "Go",
  "pulseReport.hifzPulse": "Hifz Pulse",
  "pulseReport.sWeek": "{name}'s week",
  "pulseReport.activeDays": "Active days",
  "pulseReport.newAyahs": "New ayahs",
  "pulseReport.halaqaAttended": "Halaqa attended",
  "pulseReport.recitationTests": "Recitation tests",
  "pulseReport.revisionGoals": "Revision & Goals",
  "pulseReport.dailyGoalAyahs": "Daily goal (ayahs)",
  "pulseReport.reviewsThisWeek": "Reviews this week",
  "pulseReport.text": "{avg_review_accuracy}%",
  "pulseReport.avgReviewScore": "Avg review score",
  "pulseReport.dueToday": "Due today",
  "pulseReport.newBadges": "New badges:",
  "pulseReport.dailyActivity": "Daily activity",
  "pulseReport.ayahsMarkedPerDay": "Ayahs marked per day",
  "pulseReport.recitationAccuracy": "Recitation accuracy",
  "pulseReport.noRecitationTestsThisWeek": "No recitation tests this week. Begin with a short surah and your trend will appear here.",
  "pulseReport.firstWeekOfTests": "first week of tests",
  "pulseReport.vsLastWeek": "+{accuracy_trend_delta}% vs last week",
  "pulseReport.vsLastWeek2": "{accuracy_trend_delta}% vs last week",
  "pulseReport.steady": "steady",
  "pulseReport.matchScore": "Match score ·",
  "pulseReport.prevWeek": "· Prev week: {prev_avg_accuracy}%",
  "pulseReport.top3MistakeWords": "Top 3 mistake words",
  "pulseReport.noDataYetTryA": "No data yet — try a recitation test.",
  "pulseReport.noRepeatingMistakesMashallah": "No repeating mistakes — Mashallah!",
  "pulseReport.stumbled": "Stumbled",
  "pulseReport.surahsYouVisited": "Surahs you visited",
  "pulseReport.aNoteForTheWeek": "A note for the week",
  "parentContactCard.parentContactSaved": "Parent contact saved",
  "parentContactCard.parentContactCleared": "Parent contact cleared",
  "parentContactCard.couldnTClear": "Couldn't clear",
  "parentContactCard.weeklyHifzPulseForYour": "Weekly Hifz Pulse for your parent",
  "parentContactCard.everyFridayMorningYourParent": "Every Friday morning, your parent gets a short email with your week's accuracy, ayahs memorised, and top mistakes. Encouraging family follow-up boosts hifz outcomes",
  "parentContactCard.aLot": "a lot",
  "parentContactCard.sendingTo": "Sending to",
  "parentContactCard.change": "Change",
  "parentContactCard.stopSending": "Stop sending",
  "parentContactCard.addParentEmail": "Add parent email",
  "parentContactCard.parentExampleCom": "parent@example.com",
  "parentContactCard.sendTheWeeklyPulseDigest": "Send the weekly Pulse digest to this email",
  "parentContactCard.save": "Save",
  "socialShare.cardDownloadedPerfectForInstagram": "Card downloaded — perfect for Instagram stories!",
  "socialShare.shareInspire": "Share & inspire",
  "socialShare.image": "Image",
  "socialShare.copyText": "Copy text",
  "socialShare.more": "More…",
  "socialShare.forInstagramDownloadTheImage": "For Instagram: download the image, then post it to your story or feed.",
  "hubPage.open": "Open",
  "mistakeHeatmap.mistakeHeatmap": "Mistake heatmap",
  "mistakeHeatmap.eachWordColorCodedBy": "Each word color-coded by how well it matched your recitation.",
  "mistakeHeatmap.words": "{wordDiffCount} words",
  "mistakeHeatmap.extraWordsWeHeard": "Extra words we heard ({extrasCount})",
  "mistakeHeatmap.theseWereTranscribedButDon": "These were transcribed but don't belong to this ayah. Could be background noise or a slip.",
  "madaniPage.uthmani": "Uthmani",
  "madaniPage.indoPak": "Indo-Pak",
  "madaniPage.hafs": "Hafs",
  "madaniPage.hafsTajweed": "Hafs Tajweed",
  "mistakeHeatmap.correct": "Correct",
  "mistakeHeatmap.closeReviseTajweed": "Close · revise tajweed",
  "mistakeHeatmap.mismatch": "Mismatch",
  "mistakeHeatmap.notRecited": "Not recited",
  "practiceMistakesDrill.closeRevise": "Close · revise",
  "bookings.trialClass": "Trial class",
  "bookings.oneToOne": "One-to-one",
  "community.completed": "Completed",
  "community.inProgress": "In progress",
  "community.needsEncouragement": "Needs encouragement",
  "hifzTest.microphoneAccessIsBlockedAllow": "Microphone access is blocked — allow it in your browser's site settings, then try again.",
  "hifzTest.speechRecognitionIsBlockedIn": "Speech recognition is blocked in this browser — allow the microphone and try again.",
  "hifzTest.noMicrophoneFoundOrAnother": "No microphone found, or another app is using it.",
  "hifzTest.canTReachTheSpeech": "Can't reach the speech service — check your internet connection.",
  "hifzTest.thisBrowserCanTRecognise": "This browser can't recognise Arabic speech. Try Chrome or Edge.",
  "hifzTest.speechRecognitionWasInterruptedTap": "Speech recognition was interrupted — tap Resume to continue.",
  "hifzTest.memorizeNew": "Memorize new",
  "hifzTest.openTheHifzPlanTo": "Open the Hifz Plan to start fresh ayahs.",
  "hifzTest.reviewOld": "Review old",
  "hifzTest.revisitMemorizedAyahsViaThe": "Revisit memorized ayahs via the Qari loop.",
  "hifzTest.aiRecitationTest": "AI recitation test",
  "hifzTest.reciteTheFullRangeIn": "Recite the full range in one take — your AI coach scores Hifz + Tajweed and explains every mistake.",
  "hifzTest.selfTestPractice": "Self test & practice",
  "hifzTest.allAyahsHiddenRevealOne": "All ayahs hidden — reveal one at a time or hover to peek, or tap the mic to recite live for instant feedback.",
  "hifzTest.off": "Off",
  "hifzTest.waitForMe": "Wait for me",
  "hifzTest.aggressive": "Aggressive",
  "hifzTest.balanced": "Balanced",
  "hifzTest.patient": "Patient",
  "profile.under13": "Under 13",
  "profile.preferNotToSay": "Prefer not to say",
  "profile.dailyGoals": "Daily Goals",
  "profile.adjustYourPaceAnytimeAyahs": "Adjust your pace anytime — ayahs, pages or minutes.",
  "profile.timelineOfEverythingYouVe": "Timeline of everything you've memorized and revised.",
  "profile.achievementsInvites": "Achievements & Invites",
  "profile.yourBadgesLevelAndFriends": "Your badges, level, and friends you've invited.",
  "profile.aiInsights": "AI Insights",
  "profile.yourWeeklyPulseTrendsAccuracy": "Your weekly pulse — trends, accuracy and coach notes.",
  "profile.mySubmissions": "My Submissions",
  "profile.recitationsYouVeSentTo": "Recitations you've sent to your teacher.",
  "progress.notStarted": "Not started",
  "progress.ayah": "Ayah",
  "progress.status": "Status",
  "progress.revisions": "Revisions",
  "progress.lastRevised": "Last Revised",
  "progress.mastery": "Mastery",
  "progress.aiScore": "AI Score",
  "progress.date": "Date",
  "progress.revisionDone": "Revision done",
  "revision.recovery": "Recovery",
  "revision.overdue": "Overdue",
  "revision.recentMemorization": "Recent memorization",
  "revision.longTermRevision": "Long-term revision",
  "revision.perfect": "Perfect",
  "revision.good": "Good",
  "revision.shaky": "Shaky",
  "revision.forgot": "Forgot",
  "revision.3Ayahs": "3 ayahs",
  "revision.5Ayahs": "5 ayahs",
  "revision.page": "¼ page",
  "revision.page2": "½ page",
  "revision.page3": "¾ page",
  "revision.1Page": "1 page",
  "revision.20Min": "20 min",
  "revision.30Min": "30 min",
  "teachers.verifiedIdentity": "Verified Identity",
  "teachers.verifiedQualifications": "Verified Qualifications",
  "teachers.verifiedIjazah": "Verified Ijazah",
  "teachers.institutionVerified": "Institution Verified",
  "teachers.masterTeacher": "Master Teacher",
  "teachers.womenSInstructors": "👩‍🏫 Women's Instructors",
  "teachers.kidsSpecialists": "🧒 Kids Specialists",
  "teachers.ijazahHolders": "🟣 Ijazah Holders",
};
